<template lang="pug">
.checkbox-wrapper
  label.checkbox-label
    input.checkbox-input.mr-1(
      type="checkbox",
      :id="id",
      :class="{ active: val, disabled: disabled }",
      :checked="val",
      :disabled="disabled",
      :aria-label="ariaLabel",
      :aria-labelledby="ariaLabelledby",
      :aria-describedby="ariaDescribedby",
      :aria-invalid="ariaInvalid",
      :aria-required="ariaRequired",
      :required="ariaRequired",
      @change="toggleChecker"
    )
    span.checkbox-control.mr-1(aria-hidden="true")
    slot
</template>
<script setup lang="ts">
import { ref, watch } from "vue";

defineOptions({ name: "CheckboxInput" });

interface Props {
  modelValue?: boolean;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  ariaInvalid?: boolean;
  ariaRequired?: boolean;
  // Retains legacy behaviour: when false, external model changes are emitted again.
  showExternalState?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  disabled: false,
  ariaInvalid: false,
  ariaRequired: false,
  showExternalState: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const val = ref(props.modelValue);

watch(val, (value) => {
  if (!props.showExternalState) {
    emit("update:modelValue", value);
  }
});

watch(
  () => props.modelValue,
  (value) => {
    val.value = value;
    if (!props.showExternalState) {
      emit("update:modelValue", value);
    }
  },
);

function toggleChecker(event: Event) {
  if (props.disabled) {
    return;
  }

  if (!(event.currentTarget instanceof HTMLInputElement)) {
    return;
  }

  const { checked } = event.currentTarget;

  if (props.showExternalState) {
    // The browser toggles a checkbox before it dispatches `change`. This input
    // is controlled by its external model, so restore that state until the
    // parent accepts and supplies a new `modelValue`.
    event.currentTarget.checked = props.modelValue;
    emit("update:modelValue", checked);
    return;
  }

  val.value = checked;
}
</script>
<style scoped>
.checkbox-wrapper {
  display: flex;
  align-items: center;
}

.checkbox-label {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-input {
  width: 25px;
  min-width: 25px;
  height: 25px;
  min-height: 25px;
  padding: 0;
  position: absolute;
  inset: 0 auto auto 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.checkbox-control {
  width: 25px;
  min-width: 25px;
  height: 25px;
  min-height: 25px;
  border: 2px solid CanvasText;
  border-radius: 4px;
  box-sizing: border-box;
  display: grid;
  place-content: center;
  background: Canvas;
  pointer-events: none;
}

.checkbox-control::after {
  width: 13px;
  height: 13px;
  content: "";
  background: var(--color-accent);
  opacity: 0;
}

.checkbox-input:checked + .checkbox-control,
.checkbox-input.active + .checkbox-control {
  border-color: var(--color-accent);
}

.checkbox-input:checked + .checkbox-control::after,
.checkbox-input.active + .checkbox-control::after {
  opacity: 1;
}

.checkbox-input:disabled + .checkbox-control,
.checkbox-input.disabled + .checkbox-control {
  cursor: not-allowed;
  opacity: 0.4;
}

.checkbox-input:focus-visible + .checkbox-control {
  outline: 3px solid Highlight;
  outline-offset: 2px;
}
</style>
